import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Grid,
  Button,
  TextField,
  CircularProgress,
  useTheme,
  alpha,
  Autocomplete,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";
import PaymentsIcon from "@mui/icons-material/Payments";
import NotificationSnackbar from "./NotificationBar";
const PRIMARY_COLOR = "#0a2e52";
function AddNewForcedPayment({ open, onClose, onSuccess }) {
  const theme = useTheme();
  const initialFormState = {
    payrollTypeId: "",
    employeeGroupId: "",
    payrollGroupId: "",
    costCenterId: "",
    sourceOfFund: "",
    basePay: "",
    housingAllowance: "",
    transportAllowance: "",
    otherAllowance: "",
    reason: "",
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [employeeResults, setEmployeeResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [isFetchingSettings, setIsFetchingSettings] = useState(false);
  const [entitySettings, setEntitySettings] = useState({
    payrollTypes: [],
    employeeGroups: [],
    costCenters: [],
    payrollGroups: [],
    sourceOfFunds: [],
  });

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const isFormEmpty =
    !selectedEmployee ||
    !formData.payrollTypeId ||
    !formData.employeeGroupId ||
    !formData.payrollGroupId ||
    !formData.costCenterId ||
    !formData.sourceOfFund ||
    !formData.basePay ||
    !formData.reason;

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setEmployeeResults([]);
      setSelectedEmployee(null);
      setFormData(initialFormState);
      setEntitySettings({
        payrollTypes: [],
        employeeGroups: [],
        costCenters: [],
        payrollGroups: [],
        sourceOfFunds: [],
      });
      setSnackbarOpen(false);
    }
  }, [open]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSearchEmployee = async () => {
    const term = searchTerm.trim();
    if (!term) {
      showSnackbar("Please enter a search term first.", "warning");
      setEmployeeResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setEmployeeResults([]);
      setSelectedEmployee(null);
      const res = await fetch("/api/support/find-employee-from-entity-sector", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ entitySectorId: null, searchKeyword: term }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          typeof json === "string"
            ? json
            : (json && (json.message || json.Message)) || `HTTP ${res.status}`;
        throw new Error(msg);
      }
      const list = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : json
            ? [json]
            : [];

      if (list.length > 0) {
        setEmployeeResults(list);
      } else {
        setEmployeeResults([]);
        showSnackbar("No record found", "warning");
      }
    } catch (err) {
      console.error("Employee search failed:", err);
      showSnackbar(err.message || "Search failed. Please try again.", "error");
      setEmployeeResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleEmployeeChange = async (event, newValue) => {
    setSelectedEmployee(newValue);
    setFormData(initialFormState);
    if (
      newValue &&
      newValue.entitySectorId &&
      newValue.entitySectorId.length > 0
    ) {
      const sectorIdToFetch = newValue.entitySectorId;
      fetchEntitySettings(sectorIdToFetch);
    } else {
      setEntitySettings({
        payrollTypes: [],
        employeeGroups: [],
        costCenters: [],
        payrollGroups: [],
        sourceOfFunds: [],
      });
    }
  };

  const fetchEntitySettings = async (entitySectorId) => {
    try {
      setIsFetchingSettings(true);
      const response = await fetch(
        `/api/support/entity-sector-settings?entitySectorId=${encodeURIComponent(entitySectorId)}`,
      );
      if (!response.ok) throw new Error("Failed to fetch entity settings.");
      const data = await response.json();
      setEntitySettings({
        payrollTypes: data.payrollTypes || [],
        employeeGroups: data.employeeGroups || [],
        costCenters: data.costCenters || [],
        payrollGroups: data.payrollGroups || [],
        sourceOfFunds: data.sourceOfFunds || [],
      });
    } catch (err) {
      console.error(err);
      showSnackbar("Could not load entity sector settings.", "error");
    } finally {
      setIsFetchingSettings(false);
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSettingsChange = (field, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newValue ? newValue.id : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSnackbarOpen(false);

    if (isFormEmpty) {
      showSnackbar("Please fill in all required fields.", "warning");
      return;
    }
    try {
      setLoading(true);
      const response = await fetch("/api/support/insert-forced-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employeeId: selectedEmployee.id,
          entitySectorId: selectedEmployee.entitySectorId,
          payrollTypeId: formData.payrollTypeId,
          employeeGroupId: formData.employeeGroupId,
          payrollGroupId: formData.payrollGroupId,
          sourceOfFund: formData.sourceOfFund,
          costcenterId: formData.costCenterId,
          basePay: parseFloat(formData.basePay || 0),
          housingAllowance: parseFloat(formData.housingAllowance || 0),
          transportAllowance: parseFloat(formData.transportAllowance || 0),
          otherAllowance: parseFloat(formData.otherAllowance || 0),
          reason: formData.reason,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message || "Failed to submit forced payment details.",
        );
      }
      showSnackbar("Payment Forced successfully!", "success");
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1200);
    } catch (err) {
      console.error("Error submitting details:", err);
      showSnackbar(err.message || "An unexpected error occurred.", "error");
    } finally {
      setLoading(false);
    }
  };

  const inputStyling = {
    sx: { borderRadius: 1.5, fontSize: "0.875rem" },
  };
  const labelStyling = { sx: { fontSize: "0.875rem" } };

  return (
    <>
      <Dialog
        open={open}
        onClose={!loading ? onClose : undefined}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "visible",
            backgroundColor: "#f8fafc",
          },
        }}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            px: 3,
            py: 2.5,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "white", fontWeight: 700, lineHeight: 1.2 }}
          >
            Add New Forced Payment
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={loading}
            size="small"
            sx={{
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* CONTENT */}
        <DialogContent sx={{ p: 3, overflowY: "visible" }}>
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: "text.secondary",
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            Search for an employee to auto-fill their entity, map their payroll
            settings, and input the forced payment details.
          </Typography>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PersonIcon sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
              <Typography
                variant="subtitle1"
                sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}
              >
                Employee Information
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search Employee ID,Email,Phone number or Id number"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={inputStyling}
                  InputLabelProps={labelStyling}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchEmployee();
                    }
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                md={3}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Button
                  variant="contained"
                  onClick={handleSearchEmployee}
                  disabled={isSearching || !searchTerm}
                  fullWidth
                  sx={{
                    height: "40px",
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                  startIcon={
                    isSearching ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SearchIcon fontSize="small" />
                    )
                  }
                >
                  {isSearching ? "Searching..." : "Search"}
                </Button>
              </Grid>

              {employeeResults.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    size="small"
                    options={employeeResults}
                    getOptionLabel={(option) =>
                      `${option.firstName} ${option.lastName} - ${option.id} (${option.positionName})`
                    }
                    value={selectedEmployee}
                    onChange={handleEmployeeChange}
                    PopperProps={{
                      placement: "bottom-start",
                      modifiers: [{ name: "flip", enabled: false }],
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Employee Profile"
                        required
                        InputProps={{
                          ...params.InputProps,
                          ...inputStyling.sx,
                        }}
                        InputLabelProps={labelStyling}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={employeeResults.length > 0 ? 6 : 12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Entity"
                  value={selectedEmployee ? selectedEmployee.entityName : ""}
                  disabled
                  InputProps={{
                    sx: {
                      borderRadius: 1.5,
                      fontSize: "0.875rem",
                      bgcolor: alpha(theme.palette.text.disabled, 0.05),
                    },
                  }}
                  InputLabelProps={labelStyling}
                  placeholder="Auto-filled after selection"
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <InfoIcon sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
              <Typography
                variant="subtitle1"
                sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}
              >
                Payroll Configuration
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  disabled={!selectedEmployee || isFetchingSettings}
                  options={entitySettings.payrollTypes}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    entitySettings.payrollTypes.find(
                      (opt) => opt.id === formData.payrollTypeId,
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    handleSettingsChange("payrollTypeId", newValue)
                  }
                  PopperProps={{
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: false }],
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={isFetchingSettings ? "Loading..." : "Payroll Type"}
                      required
                      InputProps={{ ...params.InputProps, ...inputStyling.sx }}
                      InputLabelProps={labelStyling}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  disabled={!selectedEmployee || isFetchingSettings}
                  options={entitySettings.employeeGroups}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    entitySettings.employeeGroups.find(
                      (opt) => opt.id === formData.employeeGroupId,
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    handleSettingsChange("employeeGroupId", newValue)
                  }
                  PopperProps={{
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: false }],
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        isFetchingSettings ? "Loading..." : "Employee Group"
                      }
                      required
                      InputProps={{ ...params.InputProps, ...inputStyling.sx }}
                      InputLabelProps={labelStyling}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  disabled={!selectedEmployee || isFetchingSettings}
                  options={entitySettings.payrollGroups}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    entitySettings.payrollGroups.find(
                      (opt) => opt.id === formData.payrollGroupId,
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    handleSettingsChange("payrollGroupId", newValue)
                  }
                  PopperProps={{
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: false }],
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        isFetchingSettings ? "Loading..." : "Payroll Group"
                      }
                      required
                      InputProps={{ ...params.InputProps, ...inputStyling.sx }}
                      InputLabelProps={labelStyling}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  disabled={!selectedEmployee || isFetchingSettings}
                  options={entitySettings.costCenters}
                  getOptionLabel={(option) =>
                    option ? `${option.id} - ${option.name}` : ""
                  }
                  value={
                    entitySettings.costCenters.find(
                      (opt) => opt.id === formData.costCenterId,
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    handleSettingsChange("costCenterId", newValue)
                  }
                  PopperProps={{
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: false }],
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={isFetchingSettings ? "Loading..." : "Cost Center"}
                      required
                      InputProps={{
                        ...params.InputProps,
                        ...inputStyling.sx,
                      }}
                      InputLabelProps={labelStyling}
                    />
                  )}
                />
              </Grid>

              {/* Added Source of Fund Form element */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  disabled={!selectedEmployee || isFetchingSettings}
                  options={entitySettings.sourceOfFunds}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    entitySettings.sourceOfFunds.find(
                      (opt) => opt.id === formData.sourceOfFund,
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    handleSettingsChange("sourceOfFund", newValue)
                  }
                  PopperProps={{
                    placement: "bottom-start",
                    modifiers: [{ name: "flip", enabled: false }],
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        isFetchingSettings ? "Loading..." : "Source of Fund"
                      }
                      required
                      InputProps={{ ...params.InputProps, ...inputStyling.sx }}
                      InputLabelProps={labelStyling}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 3: Financial Details */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PaymentsIcon sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
              <Typography
                variant="subtitle1"
                sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}
              >
                Compensation Details
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Base Pay"
                  name="basePay"
                  type="number"
                  value={formData.basePay}
                  onChange={handleChange}
                  disabled={!selectedEmployee}
                  InputProps={inputStyling}
                  InputLabelProps={labelStyling}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Housing Allowance"
                  name="housingAllowance"
                  type="number"
                  value={formData.housingAllowance}
                  onChange={handleChange}
                  disabled={!selectedEmployee}
                  InputProps={inputStyling}
                  InputLabelProps={labelStyling}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Transport Allowance"
                  name="transportAllowance"
                  type="number"
                  value={formData.transportAllowance}
                  onChange={handleChange}
                  disabled={!selectedEmployee}
                  InputProps={inputStyling}
                  InputLabelProps={labelStyling}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Other Allowance"
                  name="otherAllowance"
                  type="number"
                  value={formData.otherAllowance}
                  onChange={handleChange}
                  disabled={!selectedEmployee}
                  InputProps={inputStyling}
                  InputLabelProps={labelStyling}
                />
              </Grid>

              {/* Reason Field */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  multiline
                  rows={2}
                  label="Reason for Forced Payment"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  disabled={!selectedEmployee}
                  placeholder="Provide Reason for Forcing Payment.."
                  InputProps={inputStyling}
                  InputLabelProps={labelStyling}
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 4,
                pt: 2,
                borderTop: "1px solid #e8edf3",
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={loading}
                size="small"
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.text.primary, 0.05),
                    borderColor: alpha(theme.palette.text.primary, 0.3),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={loading || isFetchingSettings || isFormEmpty}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: PRIMARY_COLOR,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#082541",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save Payment"
                )}
              </Button>
            </Box>
          </form>
        </DialogContent>
        <NotificationSnackbar
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          severity={snackbarSeverity}
        />
      </Dialog>
    </>
  );
}

export default AddNewForcedPayment;
